import { useEffect, useMemo, useRef } from 'react';
import { PHOTO_ACCEPTED_MIME } from '@camper-diary/shared';

type Props = {
  files: File[];
  onChange: (files: File[]) => void;
};

/**
 * Picker offline: tiene i File in stato controllato dal padre, mostra preview
 * via object URL e bottoni separati per fotocamera (capture=environment) e galleria.
 * I file vengono caricati realmente solo dopo la creazione del POI (handler nel form).
 */
export function PoiPhotosPicker({ files, onChange }: Props) {
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // object URL per preview, ricreati a ogni cambio della lista files
  const previews = useMemo(
    () => files.map((f) => ({ key: keyOf(f), url: URL.createObjectURL(f), name: f.name })),
    [files],
  );

  // cleanup degli object URL alla dismissione o al cambio della lista
  useEffect(() => {
    return () => {
      for (const p of previews) URL.revokeObjectURL(p.url);
    };
  }, [previews]);

  function appendFiles(list: FileList | File[]) {
    const incoming = Array.from(list);
    if (incoming.length === 0) return;
    // dedup per nome+size+lastModified
    const existing = new Set(files.map(keyOf));
    const merged = [...files, ...incoming.filter((f) => !existing.has(keyOf(f)))];
    onChange(merged);
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) appendFiles(e.target.files);
    if (e.target) e.target.value = '';
  }

  function removeAt(idx: number) {
    const next = files.slice();
    next.splice(idx, 1);
    onChange(next);
  }

  return (
    <div className="poi-photos">
      <div className="poi-photos-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => cameraInputRef.current?.click()}
        >
          📷 Scatta foto
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => galleryInputRef.current?.click()}
        >
          🖼️ Da galleria
        </button>
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={onInputChange}
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept={PHOTO_ACCEPTED_MIME.join(',')}
          multiple
          hidden
          onChange={onInputChange}
        />
      </div>
      <p className="muted small">
        JPEG, PNG, WebP, GIF · max 25 MB per foto · verranno caricate al salvataggio del POI
      </p>

      {previews.length > 0 && (
        <ul className="photo-grid">
          {previews.map((p, i) => (
            <li key={p.key} className="photo-thumb">
              <img src={p.url} alt={p.name} loading="lazy" />
              <button
                type="button"
                className="thumb-delete"
                onClick={() => removeAt(i)}
                aria-label="Rimuovi dalla coda"
                title="Rimuovi dalla coda"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function keyOf(f: File): string {
  return `${f.name}:${f.size}:${f.lastModified}`;
}
